/**
 * Remappable keyboard bindings for the race controls.
 *
 * Every action owns a primary key (rebindable from the CONTROLS tab) and an
 * optional fixed secondary key, so the arrow keys keep working for players who
 * rebind WASD. Bindings are stored per device in localStorage and shared by
 * the InputController, the camera shortcut and every on-screen key hint.
 */
import { t, tc } from './i18n/index.js';

export type KeyAction =
  | 'throttle'
  | 'brake'
  | 'left'
  | 'right'
  | 'handbrake'
  | 'nitro'
  | 'reset'
  | 'camera'
  | 'bank'
  | 'turretLeft'
  | 'turretRight'
  | 'fire';

export interface KeyBinding {
  primary: string;
  secondary: string | null;
}

export type KeyboardBindings = Record<KeyAction, KeyBinding>;

export const KEY_ACTIONS: readonly KeyAction[] = [
  'throttle', 'brake', 'left', 'right', 'handbrake', 'nitro',
  'reset', 'camera', 'bank', 'turretLeft', 'turretRight', 'fire',
];

export const KEY_ACTION_LABELS: Record<KeyAction, string> = {
  throttle: 'ACCELERATE',
  brake: 'BRAKE / REVERSE',
  left: 'STEER LEFT',
  right: 'STEER RIGHT',
  handbrake: 'DRIFT',
  nitro: 'NITRO',
  reset: 'RECOVER',
  camera: 'CAMERA',
  bank: 'BANK HEAT',
  turretLeft: 'TURRET LEFT',
  turretRight: 'TURRET RIGHT',
  fire: 'FIRE',
};

const DEFAULT_BINDINGS: KeyboardBindings = {
  throttle: { primary: 'KeyW', secondary: 'ArrowUp' },
  brake: { primary: 'KeyS', secondary: 'ArrowDown' },
  left: { primary: 'KeyA', secondary: 'ArrowLeft' },
  right: { primary: 'KeyD', secondary: 'ArrowRight' },
  handbrake: { primary: 'Space', secondary: null },
  nitro: { primary: 'ShiftLeft', secondary: 'ShiftRight' },
  reset: { primary: 'KeyR', secondary: null },
  camera: { primary: 'KeyC', secondary: null },
  bank: { primary: 'KeyB', secondary: null },
  turretLeft: { primary: 'KeyQ', secondary: null },
  turretRight: { primary: 'KeyE', secondary: null },
  fire: { primary: 'KeyF', secondary: null },
};

/** Keys owned by the shell (pause, sound, telemetry, quick restart). */
const RESERVED_CODES = new Set(['Escape', 'KeyM', 'F8', 'Backspace', 'Tab', 'Enter', 'MetaLeft', 'MetaRight']);
const STORAGE_KEY = 'nightshift:keyboard-bindings:v1';

let cached: KeyboardBindings | null = null;
const listeners = new Set<(bindings: KeyboardBindings) => void>();

export function defaultKeyboardBindings(): KeyboardBindings {
  return cloneBindings(DEFAULT_BINDINGS);
}

export function keyboardBindings(): KeyboardBindings {
  if (!cached) cached = loadBindings();
  return cached;
}

export function keyCodesFor(action: KeyAction): readonly string[] {
  const binding = keyboardBindings()[action];
  return binding.secondary ? [binding.primary, binding.secondary] : [binding.primary];
}

export function actionForKeyCode(code: string): KeyAction | null {
  const bindings = keyboardBindings();
  for (const action of KEY_ACTIONS) {
    const binding = bindings[action];
    if (binding.primary === code || binding.secondary === code) return action;
  }
  return null;
}

export function isReservedKeyCode(code: string): boolean {
  return RESERVED_CODES.has(code);
}

/**
 * Assigns a new primary key. A key already used by another action is swapped
 * onto that action, so a rebind can never leave two actions on one key or an
 * action with no key at all.
 */
export function setKeyboardBinding(action: KeyAction, code: string): KeyboardBindings | null {
  if (!code || isReservedKeyCode(code)) return null;
  const next = cloneBindings(keyboardBindings());
  const previous = next[action].primary;
  for (const other of KEY_ACTIONS) {
    if (other === action) continue;
    if (next[other].primary === code) next[other].primary = previous;
    if (next[other].secondary === code) next[other].secondary = null;
  }
  if (next[action].secondary === code) next[action].secondary = null;
  next[action].primary = code;
  commit(next);
  return next;
}

export function resetKeyboardBindings(): KeyboardBindings {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // The in-memory defaults still apply.
  }
  const next = defaultKeyboardBindings();
  cached = next;
  emit(next);
  return next;
}

export function onKeyboardBindingsChange(listener: (bindings: KeyboardBindings) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function keyCodeLabel(code: string): string {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `NUM ${code.slice(6)}`;
  const named: Record<string, string> = {
    ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
    Space: 'SPACE', ShiftLeft: 'SHIFT', ShiftRight: 'R-SHIFT', ControlLeft: 'CTRL', ControlRight: 'R-CTRL',
    AltLeft: 'ALT', AltRight: 'ALT GR', CapsLock: 'CAPS', Backquote: '`', Minus: '-', Equal: '=',
    BracketLeft: '[', BracketRight: ']', Semicolon: ';', Quote: "'", Backslash: '\\', Comma: ',',
    Period: '.', Slash: '/', Delete: 'DEL', Insert: 'INS', Home: 'HOME', End: 'END',
    PageUp: 'PG UP', PageDown: 'PG DN', NumpadEnter: 'NUM ↵', NumpadAdd: 'NUM +', NumpadSubtract: 'NUM -',
    NumpadMultiply: 'NUM *', NumpadDivide: 'NUM /', NumpadDecimal: 'NUM .',
  };
  const label = named[code];
  // Word keycaps follow the printed legend of local keyboards (Strg, Entf …).
  if (label) return /[A-Z]{2}/.test(label) ? tc('key', label) : label;
  return code.replace(/^(Numpad|Key|Digit)/, '').toUpperCase().slice(0, 7);
}

export function parseKeyboardBindings(raw: string | null): KeyboardBindings {
  const defaults = defaultKeyboardBindings();
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return defaults;
    const used = new Set<string>();
    const result = defaultKeyboardBindings();
    for (const action of KEY_ACTIONS) {
      const candidate = (parsed as Record<string, unknown>)[action] as Partial<KeyBinding> | undefined;
      const primary = typeof candidate?.primary === 'string' && validCode(candidate.primary) ? candidate.primary : null;
      if (primary && !used.has(primary)) result[action].primary = primary;
      used.add(result[action].primary);
    }
    // Secondaries stay fixed defaults unless a primary took them over.
    for (const action of KEY_ACTIONS) {
      const secondary = result[action].secondary;
      if (secondary && KEY_ACTIONS.some((other) => result[other].primary === secondary)) result[action].secondary = null;
    }
    return result;
  } catch {
    return defaults;
  }
}

function validCode(code: string): boolean {
  return code.length > 0 && code.length <= 24 && /^[A-Za-z0-9]+$/.test(code) && !isReservedKeyCode(code);
}

function loadBindings(): KeyboardBindings {
  try {
    return parseKeyboardBindings(localStorage.getItem(STORAGE_KEY));
  } catch {
    return defaultKeyboardBindings();
  }
}

function commit(next: KeyboardBindings): void {
  cached = next;
  try {
    const primaries = Object.fromEntries(KEY_ACTIONS.map((action) => [action, { primary: next[action].primary }]));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(primaries));
  } catch {
    // Rebinds still apply for this session without persistence.
  }
  emit(next);
}

function emit(next: KeyboardBindings): void {
  for (const listener of listeners) listener(next);
}

function cloneBindings(bindings: KeyboardBindings): KeyboardBindings {
  return Object.fromEntries(
    KEY_ACTIONS.map((action) => [action, { ...bindings[action] }]),
  ) as KeyboardBindings;
}
